/**
 * Toast Notification Service
 *
 * File: Mythbound.App/src/services/toast/toastService.ts
 * Created: 2026-01-07
 * Last Modified: 2026-01-07
 *
 * PURPOSE:
 * Centralized toast notification service for user feedback.
 * Provides consistent notification UX across all components.
 *
 * USAGE:
 * ```typescript
 * import { toastService } from '../../services/toast/toastService';
 * 
 * toastService.success('Character created successfully!');
 * toastService.error('Failed to load campaign');
 * toastService.warning('Campaign over tier limit (5/4)');
 * toastService.info('Session started on Discord');
 * ```
 *
 * IMPLEMENTATION OPTIONS:
 * 1. Chakra UI Toast (if already using Chakra)
 * 2. react-toastify (popular, standalone)
 * 3. Custom implementation (lightweight)
 *
 * Current: Custom implementation (no dependencies)
 * Future: Replace with Chakra UI toast for consistency
 *
 * Pipeline: Components → toastService → DOM toast container
 * Dependencies: None (vanilla JS for now)
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  message: string;
  type: ToastType;
  duration?: number; // milliseconds, default 3000
  position?: 'top-right' | 'top-center' | 'bottom-right' | 'bottom-center';
}

class ToastService {
  private container: HTMLDivElement | null = null;
  private toasts: Map<string, HTMLDivElement> = new Map();

  /**
   * Initialize toast container on first use
   */
  private ensureContainer(): void {
    if (this.container) return;

    this.container = document.createElement('div');
    this.container.id = 'toast-container';
    this.container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 12px;
      pointer-events: none;
    `;
    document.body.appendChild(this.container);
  }

  /**
   * Show a toast notification
   */
  private show(options: ToastOptions): void {
    this.ensureContainer();
    if (!this.container) return;

    const { message, type, duration = 3000, position = 'top-right' } = options;
    const toastId = `toast-${Date.now()}-${Math.random()}`;

    // Create toast element
    const toast = document.createElement('div');
    toast.id = toastId;
    toast.style.cssText = `
      background: ${this.getBackgroundColor(type)};
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      max-width: 350px;
      word-wrap: break-word;
      pointer-events: auto;
      cursor: pointer;
      animation: slideIn 0.3s ease-out;
      transition: opacity 0.3s, transform 0.3s;
    `;

    // Add icon
    const icon = this.getIcon(type);
    toast.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <span style="font-size: 20px;">${icon}</span>
        <span>${message}</span>
      </div>
    `;

    // Click to dismiss
    toast.addEventListener('click', () => {
      this.dismiss(toastId);
    });

    // Add to container
    this.container.appendChild(toast);
    this.toasts.set(toastId, toast);

    // Auto-dismiss after duration
    setTimeout(() => {
      this.dismiss(toastId);
    }, duration);
  }

  /**
   * Dismiss a toast
   */
  private dismiss(toastId: string): void {
    const toast = this.toasts.get(toastId);
    if (!toast) return;

    // Fade out animation
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(100px)';

    setTimeout(() => {
      toast.remove();
      this.toasts.delete(toastId);

      // Clean up container if empty
      if (this.toasts.size === 0 && this.container) {
        this.container.remove();
        this.container = null;
      }
    }, 300);
  }

  /**
   * Get background color for toast type
   */
  private getBackgroundColor(type: ToastType): string {
    switch (type) {
      case 'success':
        return '#4caf50'; // Green
      case 'error':
        return '#f44336'; // Red
      case 'warning':
        return '#ff9800'; // Orange
      case 'info':
        return '#2196f3'; // Blue
      default:
        return '#333'; // Dark gray
    }
  }

  /**
   * Get icon for toast type
   */
  private getIcon(type: ToastType): string {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '•';
    }
  }

  // Public API

  /**
   * Show success toast (green)
   * @example toastService.success('Campaign created!');
   */
  success(message: string, duration?: number): void {
    this.show({ message, type: 'success', duration });
  }

  /**
   * Show error toast (red)
   * @example toastService.error('Failed to save character');
   */
  error(message: string, duration?: number): void {
    this.show({ message, type: 'error', duration: duration || 5000 }); // Errors stay longer
  }

  /**
   * Show warning toast (orange)
   * @example toastService.warning('Campaign over tier limit');
   */
  warning(message: string, duration?: number): void {
    this.show({ message, type: 'warning', duration: duration || 4000 });
  }

  /**
   * Show info toast (blue)
   * @example toastService.info('Session started on Discord');
   */
  info(message: string, duration?: number): void {
    this.show({ message, type: 'info', duration });
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    const toastIds = Array.from(this.toasts.keys());
    toastIds.forEach(id => this.dismiss(id));
  }
}

// Singleton instance
export const toastService = new ToastService();

// Add CSS animation (inject once)
if (typeof document !== 'undefined') {
  const styleId = 'toast-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateX(100px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

